import React, { useMemo } from 'react';
import { Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../components/common/CrudPage';
import feedbackFeatureConfigService from '../../services/survey/feedbackFeatureConfigService';
import { listActiveFeaturesRequest } from '../../services/cms/featureServiceRequest';

const { Text } = Typography;

// Danh mục tính năng trong popup "Chọn tính năng" của màn Góp ý trên app.
// App hiển thị tính năng trả phí trước, miễn phí sau, trong mỗi nhóm theo thứ tự ưu tiên.
const FeedbackFeatureConfigManagement = () => {
  const { data: rawFeatures } = useQuery({
    queryKey: ['activeFeaturesForFeedback'],
    queryFn: () => listActiveFeaturesRequest(),
  });

  const featureOptions = useMemo(() => {
    const list = Array.isArray(rawFeatures)
      ? rawFeatures
      : (Array.isArray(rawFeatures?.data) ? rawFeatures.data : []);
    return list.map((item) => ({
      label: `[#${item.id}] ${item.name}`,
      value: item.id,
      name: item.name,
    }));
  }, [rawFeatures]);

  const filterFields = useMemo(
    () => [
      {
        label: 'Trạng thái',
        value: 'status',
        options: [
          { label: 'Tất cả trạng thái', value: '' },
          { label: 'Hiển thị', value: '1' },
          { label: 'Ẩn', value: '0' },
        ],
      },
      {
        label: 'Loại tính năng',
        value: 'is_paid',
        options: [
          { label: 'Tất cả', value: '' },
          { label: 'Trả phí', value: '1' },
          { label: 'Miễn phí', value: '0' },
        ],
      },
    ],
    []
  );

  const formFields = useMemo(
    () => [
      {
        name: 'feature_id',
        label: 'Tính năng liên kết',
        type: 'select',
        rules: [{ required: true, message: 'Vui lòng chọn tính năng!' }],
        placeholder: 'Chọn tính năng từ danh mục...',
        propsInput: {
          showSearch: true,
          allowClear: true,
          optionFilterProp: 'label',
        },
        options: featureOptions,
        md: 12,
      },
      {
        name: 'feature_name',
        label: 'Tên hiển thị trong popup',
        type: 'input',
        placeholder: 'Để trống sẽ tự động lấy tên tính năng đã chọn',
        md: 12,
      },
      {
        name: 'icon',
        label: 'Icon',
        type: 'upload-cdn',
        uploadFolder: 'feedback',
        enterLink: true,
        linkPlaceholder: 'Hoặc dán link icon',
        md: 24,
      },
      {
        name: 'is_paid',
        label: 'Loại tính năng',
        type: 'select',
        options: [
          { label: 'Trả phí (xếp trước)', value: 1 },
          { label: 'Miễn phí', value: 0 },
        ],
        defaultValue: 0,
        md: 8,
      },
      {
        name: 'sort_order',
        label: 'Thứ tự ưu tiên trong nhóm',
        type: 'number',
        placeholder: 'Nhỏ hơn xếp trước',
        defaultValue: 0,
        propsInput: { min: 0, className: 'w-full' },
        md: 8,
      },
      {
        name: 'status',
        label: 'Trạng thái',
        type: 'select',
        options: [
          { label: 'Hiển thị', value: 1 },
          { label: 'Ẩn', value: 0 },
        ],
        defaultValue: 1,
        md: 8,
      },
    ],
    [featureOptions]
  );

  const mapFormToData = (values) => {
    let featureName = values.feature_name?.trim();
    if (!featureName && values.feature_id) {
      const matched = featureOptions.find((f) => f.value === Number(values.feature_id));
      featureName = matched ? matched.name : '';
    }
    return {
      ...values,
      feature_id: Number(values.feature_id),
      feature_name: featureName || (values.feature_id ? `Tính năng #${values.feature_id}` : ''),
      is_paid: Number(values.is_paid) === 1 ? 1 : 0,
      sort_order: values.sort_order !== undefined && values.sort_order !== null ? Number(values.sort_order) : 0,
      status: values.status !== undefined && values.status !== null ? Number(values.status) : 1,
    };
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID TÍNH NĂNG',
        dataIndex: 'feature_id',
        key: 'feature_id',
        width: 140,
        align: 'center',
        render: (fId) => <Tag color="blue" className="font-mono text-sm font-semibold">#{fId}</Tag>,
      },
      {
        title: 'ICON',
        dataIndex: 'icon',
        key: 'icon',
        width: 80,
        align: 'center',
        render: (url) =>
          url ? <img src={url} alt="" style={{ width: 32, height: 32, objectFit: 'contain', margin: '0 auto' }} /> : '-',
      },
      {
        title: 'TÊN HIỂN THỊ',
        dataIndex: 'feature_name',
        key: 'feature_name',
        render: (name) => <Text strong>{name}</Text>,
      },
      {
        title: 'LOẠI',
        dataIndex: 'is_paid',
        key: 'is_paid',
        width: 130,
        align: 'center',
        render: (value) => (
          <Tag color={Number(value) === 1 ? 'gold' : 'default'}>
            {Number(value) === 1 ? 'TRẢ PHÍ' : 'MIỄN PHÍ'}
          </Tag>
        ),
      },
      {
        title: 'THỨ TỰ',
        dataIndex: 'sort_order',
        key: 'sort_order',
        width: 100,
        align: 'center',
      },
      {
        title: 'TRẠNG THÁI',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        align: 'center',
        render: (status) => (
          <Tag color={Number(status) === 1 ? 'green' : 'red'}>
            {Number(status) === 1 ? 'HIỂN THỊ' : 'ẨN'}
          </Tag>
        ),
      },
      {
        title: 'CẬP NHẬT',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 160,
        render: (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
      },
    ],
    []
  );

  return (
    <CrudPage
      title="DANH MỤC TÍNH NĂNG GÓP Ý"
      subTitle="Tính năng hiển thị trong popup chọn tính năng của màn Góp ý trên app — trả phí xếp trước, trong mỗi nhóm theo thứ tự ưu tiên"
      itemsName="Tính năng góp ý"
      queryKey="feedbackFeatureConfigList"
      service={feedbackFeatureConfigService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      mapFormToData={mapFormToData}
      searchPlaceholder="Tìm kiếm tên tính năng..."
      addButtonLabel="Thêm tính năng"
      disableCopy
      formConfig={{ width: 800 }}
    />
  );
};

export default FeedbackFeatureConfigManagement;
